import * as React from 'react';
import { useMutation, useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { Row } from '@tanstack/react-table';
import type { Invoice } from 'contracts/tracking-v2/invoices';
import { TRACKING_V2_ROUTES, SCHEDULED_REPORTS_ROUTES } from 'lib/routes';
import { notify } from 'services/notifications';
import { useFetchPaginatedInvoices } from 'hooks/api/invoices';
import { useCreateScheduledReport } from 'hooks/api/scheduled-reports';
import {
  useInvoicesTrackingSearchParams,
} from 'hooks/store/invoices/use-invoices-tracking-search-params';
import { Scaffold } from 'layouts';
import { PageHeaderForFilters } from 'components/ui/page-header-for-filters';
import { DataTableV2, type SortingProps } from 'components/ui/DataTableV2';
import {
  TabbedNavigation,
  ClearFiltersButton,
} from 'pages/tracking-v2/shared-components';

import { useInvoiceColumns } from './hooks';
import {
  PageTitle,
  InvoiceFilters,
  type InvoiceFiltersRef,
  InvoiceIndicators,
  RefineReportParamsDrawer,
  type RefineReportParamsDrawerRef,
  type OnSubmitRefineReportParams,
} from './components';

export const InvoicesTrackingPage = () => {
  const navigate = useNavigate();
  const {
    filters,
    paginationControl,
    updateFilters,
    updatePaginationControl,
    clearState,
  } = useInvoicesTrackingSearchParams({ persisted: true });
  const { fetchPaginatedInvoices } = useFetchPaginatedInvoices();
  const { createScheduledReport } = useCreateScheduledReport();

  const filtersRef = React.useRef<InvoiceFiltersRef>(null);
  const refineReportDrawerRef = React.useRef<RefineReportParamsDrawerRef>(null);

  const someFiltersAreActive = React.useMemo(() => {
    const { sameCarrierCnpj, ...rest } = filters;
    return Object.values(rest).some(value => !!value);
  }, [filters]);

  const columns = useInvoiceColumns();

  const invoicesQuery = useQuery({
    queryKey: ['fetch-paginated-invoices', filters, paginationControl],
    queryFn: () => {
      return fetchPaginatedInvoices({
        filters,
        pagination: paginationControl,
      });
    },
    refetchOnWindowFocus: false,
  });

  const createReportMutation = useMutation({
    mutationKey: ['create-scheduled-invoices-report'],
    mutationFn: createScheduledReport,
    onSuccess: () => {
      refineReportDrawerRef.current?.close();
      notify('success', 'Relatório requisitado com sucesso.');
      navigate(SCHEDULED_REPORTS_ROUTES.invoicesTracking().url);
    },
    onError: (error: any) => {
      notify('error', error?.message);
    },
  });

  const handleFilters = React.useCallback((f: typeof filters) => {
    updateFilters(f);
    updatePaginationControl({ page: 1 });
  }, [updateFilters, updatePaginationControl]);

  const handleSort = React.useCallback((p: SortingProps) => {
    updatePaginationControl({ ...p, page: 1 });
  }, [updatePaginationControl]);

  const handleDetailsNavigation = React.useCallback((row: Row<Invoice>) => {
    const invoiceId = row.original.id;
    navigate(TRACKING_V2_ROUTES.invoiceDetails(invoiceId).url);
  }, [navigate]);

  const handleRefineReportSubmission = React.useCallback((refineParams: OnSubmitRefineReportParams) => {
    const currentFilters = filtersRef.current!.getFormData();
    createReportMutation.mutate({
      type: 'invoices',
      params: {
        ...currentFilters,
        ...refineParams,
      },
    });
  }, []);

  return (
    <Scaffold>
      <TabbedNavigation />
      <div className={'flex flex-col gap-6'}>
        <PageHeaderForFilters
          headerTitle={({ open }) => (
            <>
              <PageTitle />
              <ClearFiltersButton
                visible={!open && someFiltersAreActive}
                onClick={filtersRef.current?.resetFilters}
              />
            </>
          )}
        >
          <RefineReportParamsDrawer
            ref={refineReportDrawerRef}
            onSubmit={handleRefineReportSubmission}
            loading={createReportMutation.isLoading}
          />
          <InvoiceFilters
            ref={filtersRef}
            onSubmitFilters={handleFilters}
            onClickRequestReport={refineReportDrawerRef.current?.open}
            onResetFilters={clearState}
          />
        </PageHeaderForFilters>
        <InvoiceIndicators onSubmitFilters={handleFilters} />
        <DataTableV2
          data={invoicesQuery?.data?.data ?? []}
          columns={columns}
          manualPagination
          showControlColumns
          onPaginationChange={updatePaginationControl}
          onSort={handleSort}
          onRowClick={handleDetailsNavigation}
          isLoading={invoicesQuery.isLoading}
          pagination={{
            ...invoicesQuery?.data?.meta,
            currentPage: paginationControl.page,
            perPage: paginationControl.limit,
          }}
        />
      </div>
    </Scaffold>
  );
};